import { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import type { TFunction } from "react-i18next";
import {
  afterStateLabel,
  beforeStateLabel,
  BUYER_CUSTOM_SKU_ATTRIBUTE_NAME,
  computeSmartDiff,
  EMPTY_VALUE,
  formatValueForDisplay,
  getFirstElement,
  hasValueProperty,
  imageUrlAndDisplayText,
  isActivityHistoryValue,
  isActivityHistoryValueArray,
  isNonNullObject,
  isNullOrUndefined,
  isObject,
  isPrimitive,
  isString,
  parseImageData,
  renderHtmlWithDiff,
} from "./utils";
import { formatDate, useStoreState } from "../../../../util/util";
import { ToggleSwitchV2 } from "../../../../components/ToggleSwitch/ToggleSwitch";
import { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import useSWR from "swr";
import type {
  ActivityHistoryValue,
  AddressValue,
  CompanyHierarchyValue,
  EntityPreview,
  RelatedEntitiesValue,
  ValueType,
} from "./types";
import type { ResultItem, LinkValue } from "./types";
import {
  ChangeContainer,
  StateColumn,
  StateLabel,
  StateValue,
  DiffText,
  ImagePreview,
  ImageInfo,
  HistoryListItem,
  LinkAttr,
  ToggleContainer,
  HistoryNoImagePlaceholder,
  HistoryNoImageIcon,
  HistoryNoImageText,
  FixedCardPreview,
} from "./styles";
import { Chip } from "../../../../components/Chips/Chips";
import { useRoutePath } from "../../../../util/Routing";
import {
  ProductReference,
  EnumVariantSchema,
  Assets,
  ObjectReferenceResult,
  SupportedAssetType,
} from "../../../../types/types.PIM";
import { getObjectReferenceDetailPath } from "../../../../components/ObjectReference/ObjectReferencePreviewTooltip";
import { User } from "../../../../types/types";
import { HistoryProductCard } from "../../../../components/CardProduct/HistoryProductCard";
import type { HistoryProductCardData } from "../../../../components/CardProduct/HistoryProductCard";
import {
  AccessibilityAttributeChange,
  AssetAddedChange,
  AssetRemovedChange,
  AssetUpdatedChange,
} from "./Asset";
import {
  VisibleIcon,
  NonVisibleIcon,
  LinkIcon,
} from "../../../../components/Icons/Icons";
import { AddressTable } from "./AddressTable";
import { spacingTokens } from "../../../../designTokens";
import {
  DocumentTag,
  IconWrapper,
  TagsContainer,
} from "../../../../components/AttributeGroupFormGenerator/DocumentAttribute/DocumentAttributePreview";
import {
  GetDocumentIcon,
  get_content_type,
} from "../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import { DocumentPreview } from "../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/DocumentPreview";
import { useAuthContext } from "../../../../components/Auth";
import { endpoints } from "../../../../endpoints";
import { CompanyHierarchyActivity } from "./CompanyHierarchyActivity";
import { RelatedEntitiesActivity } from "./RelatedEntitiesActivity";

type AssetPreviewUrlSchema = {
  url: string;
  thumbnail_url?: string;
};

interface GroupedChangeProps {
  before: ActivityHistoryValue[];
  after: ActivityHistoryValue[];
  attributeName?: string;
}

export function StateColumnContent({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <StateColumn>
      <StateLabel>{label}</StateLabel>
      <StateValue>{children}</StateValue>
    </StateColumn>
  );
}

export function BeforeAfterContainer({
  attributeName,
  beforeContent,
  afterContent,
}: {
  attributeName?: string;
  beforeContent: React.ReactNode;
  afterContent: React.ReactNode;
}) {
  return (
    <ChangeContainer>
      <StateColumnContent label={beforeStateLabel(attributeName)}>
        {beforeContent}
      </StateColumnContent>
      <StateColumnContent label={afterStateLabel(attributeName)}>
        {afterContent}
      </StateColumnContent>
    </ChangeContainer>
  );
}

function renderTextWithDiff(
  before: string,
  after: string,
  isBefore: boolean
): React.ReactNode {
  const isDifferent = before !== after;
  const hasDiff = before && after && isDifferent;
  const diffParts = hasDiff
    ? computeSmartDiff(String(before), String(after))
    : [];

  if (!before && !after) {
    return EMPTY_VALUE;
  }

  const value = isBefore ? before : after;

  if (!value) {
    return EMPTY_VALUE;
  }

  if (hasDiff) {
    const filteredParts = isBefore
      ? diffParts.filter((part) => !part.added)
      : diffParts.filter((part) => !part.removed);
    return (
      <>
        {filteredParts.map((part, index) => (
          <DiffText
            key={`${isBefore ? "before" : "after"}-${index}`}
            removed={part.removed}
            added={part.added}
          >
            {part.value}
          </DiffText>
        ))}
      </>
    );
  }

  if (isDifferent) {
    if (isBefore && before && !after) {
      return <DiffText removed>{before}</DiffText>;
    }
    if (!isBefore && after && !before) {
      return <DiffText added>{after}</DiffText>;
    }
    if (isBefore && after) {
      return <DiffText removed>{before}</DiffText>;
    }
    if (!isBefore && before) {
      return <DiffText added>{after}</DiffText>;
    }
  }

  return value;
}

function attributeChange({
  before,
  after,
  attributeName,
}: {
  before: string;
  after: string;
  attributeName?: string;
}) {
  return (
    <BeforeAfterContainer
      attributeName={attributeName}
      beforeContent={renderTextWithDiff(before, after, true)}
      afterContent={renderTextWithDiff(before, after, false)}
    />
  );
}

function htmlAttributeChange({
  before,
  after,
  attributeName,
}: {
  before: string;
  after: string;
  attributeName?: string;
}) {
  return (
    <BeforeAfterContainer
      attributeName={attributeName}
      beforeContent={renderHtmlWithDiff(before, after, true)}
      afterContent={renderHtmlWithDiff(before, after, false)}
    />
  );
}

function UrlAttributeChange({
  before,
  after,
  attributeName,
}: {
  before: string;
  after: string;
  attributeName?: string;
}) {
  return (
    <BeforeAfterContainer
      attributeName={attributeName}
      beforeContent={renderLinkUrl(before, after, true)}
      afterContent={renderLinkUrl(before, after, false)}
    />
  );
}

function AssetImagePreview({
  asset,
  t,
}: {
  asset: { signedUrl?: string; previewUrl?: string };
  t: TFunction;
}) {
  const [imageUrl, setImageUrl] = useState(
    asset.signedUrl || asset.previewUrl || ""
  );

  const { data } = useSWR<AssetPreviewUrlSchema>(
    asset.signedUrl && !asset.signedUrl.includes("https") && asset.previewUrl
      ? asset.previewUrl.replace("/api", "")
      : null,
    {
      refreshInterval: 60_000,
      revalidateOnFocus: false,
      revalidateOnMount: true,
    }
  );

  useEffect(() => {
    if (data?.url) {
      setImageUrl(data.url);
    } else {
      setImageUrl(asset.signedUrl || asset.previewUrl || "");
    }
  }, [data?.url, asset.signedUrl, asset.previewUrl]);

  return imageUrl && imageUrl !== "" ? (
    <ImagePreview src={imageUrl} alt={t("Asset preview")} />
  ) : (
    <HistoryNoImagePlaceholder>
      <HistoryNoImageIcon />
      <HistoryNoImageText>No Image</HistoryNoImageText>
    </HistoryNoImagePlaceholder>
  );
}

function renderMultiSelectChips(
  variants: EnumVariantSchema[],
  isBefore: boolean,
  beforeVariants: EnumVariantSchema[],
  afterVariants: EnumVariantSchema[],
  theme: ReturnType<typeof useTheme>
): React.ReactNode {
  if (variants.length === 0) {
    return EMPTY_VALUE;
  }

  const isRemoved = (v: EnumVariantSchema): boolean => {
    return !afterVariants.some((av) => av?.id === v?.id);
  };
  const isAdded = (v: EnumVariantSchema): boolean => {
    return !beforeVariants.some((bv) => bv?.id === v?.id);
  };

  return (
    <div>
      {variants.map((variant) => {
        const shouldHighlight = isBefore
          ? isRemoved(variant)
          : isAdded(variant);
        return (
          <Chip
            name={variant?.variant}
            data-chip={JSON.stringify(variant)}
            key={variant?.id || variant?.variant}
            active={false}
            onClick={() => {}}
            style={{
              backgroundColor: shouldHighlight
                ? isBefore
                  ? theme.resolve("color_bg_surface_error_diff")
                  : theme.resolve("color_bg_surface_success_diff")
                : undefined,
              marginRight: 6,
              marginBottom: 6,
            }}
          >
            {variant?.variant}
          </Chip>
        );
      })}
    </div>
  );
}

function MultiSelectGroupedChange({
  before,
  after,
  attributeName,
}: GroupedChangeProps) {
  const theme = useTheme();
  const beforeVariants = before
    ?.map((v) => {
      return v.enum_variant || { id: v.value, variant: v.value };
    })
    .filter((v): v is EnumVariantSchema => v !== undefined);
  const afterVariants = after
    ?.map((v) => {
      return v.enum_variant || { id: v.value, variant: v.value };
    })
    .filter((v): v is EnumVariantSchema => v !== undefined);

  return (
    <BeforeAfterContainer
      attributeName={attributeName}
      beforeContent={renderMultiSelectChips(
        beforeVariants,
        true,
        beforeVariants,
        afterVariants,
        theme
      )}
      afterContent={renderMultiSelectChips(
        afterVariants,
        false,
        beforeVariants,
        afterVariants,
        theme
      )}
    />
  );
}

function renderUserChips(
  users: User[],
  isBefore: boolean,
  beforeUsers: User[],
  afterUsers: User[],
  theme: ReturnType<typeof useTheme>
): React.ReactNode {
  if (users.length === 0) {
    return EMPTY_VALUE;
  }

  const getUserDisplayName = (user: User): string => {
    const firstName = user.firstname || "";
    const lastName = user.lastname || "";
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) return fullName;
    if (user.email_address) return user.email_address;
    return "";
  };

  const isRemoved = (u: User): boolean => {
    return !afterUsers.some((au) => au?.id === u?.id);
  };
  const isAdded = (u: User): boolean => {
    return !beforeUsers.some((bu) => bu?.id === u?.id);
  };

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: spacingTokens.spacing_xs,
      }}
    >
      {users.map((user) => {
        const shouldHighlight = isBefore ? isRemoved(user) : isAdded(user);
        return (
          <Chip
            key={
              user?.id || ("email_address" in user ? user.email_address : "")
            }
            name={getUserDisplayName(user)}
            data-user={JSON.stringify(user)}
            active={false}
            onClick={() => {}}
            style={{
              backgroundColor: shouldHighlight
                ? isBefore
                  ? theme.resolve("color_bg_surface_error_diff")
                  : theme.resolve("color_bg_surface_success_diff")
                : undefined,
              marginRight: 6,
              marginBottom: 6,
            }}
          >
            {getUserDisplayName(user)}
          </Chip>
        );
      })}
    </div>
  );
}

function DocumentGroupedChange({
  before,
  after,
  attributeName,
}: GroupedChangeProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { roleIsGuest, roleIsSomeKindOfBuyer } = useAuthContext();
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

  const { data: previewAsset } = useSWR<Assets>(
    selectedAssetId
      ? endpoints.v2_tenants_id_pim_assets_id(tenant_id, selectedAssetId)
      : null,
    { revalidateOnFocus: false }
  );

  const assetForPreview = previewAsset
    ? {
        ...previewAsset,
        can_edit: false,
        can_change_visibility: false,
        can_delete: false,
        can_external_download: roleIsGuest
          ? previewAsset.visibility === "public"
          : !roleIsSomeKindOfBuyer || previewAsset.visibility !== "internal",
        can_view_details: true,
      }
    : null;

  const getAssetId = (v: ActivityHistoryValue): string => {
    const val = v.value as { asset_id?: string } | undefined;
    return val?.asset_id ?? v.id;
  };

  const isExternal = (doc: ActivityHistoryValue): boolean =>
    !!doc.host && doc.host.name !== "Agilis";

  const isRemoved = (v: ActivityHistoryValue) =>
    !after.some((a) => getAssetId(a) === getAssetId(v));
  const isAdded = (v: ActivityHistoryValue) =>
    !before.some((b) => getAssetId(b) === getAssetId(v));

  const handleClick = (doc: ActivityHistoryValue) => {
    if (isExternal(doc)) {
      const url = doc.signed_url || doc.preview_url;
      window.open(url, "_blank", "noopener noreferrer");
    } else {
      setSelectedAssetId(getAssetId(doc));
    }
  };

  const renderDocs = (docs: ActivityHistoryValue[], isBefore: boolean) => {
    if (docs.length === 0) return EMPTY_VALUE;
    return (
      <TagsContainer>
        {docs.map((doc) => {
          const highlight = isBefore ? isRemoved(doc) : isAdded(doc);
          return (
            <DocumentTag
              key={doc.id}
              onClick={() => handleClick(doc)}
              style={{
                backgroundColor: highlight
                  ? isBefore
                    ? theme.resolve("color_bg_surface_error_diff")
                    : theme.resolve("color_bg_surface_success_diff")
                  : undefined,
              }}
            >
              <IconWrapper>
                {isExternal(doc) ? (
                  <LinkIcon width={16} height={16} />
                ) : (
                  <GetDocumentIcon
                    asset_type={
                      doc.asset_type
                        ? (doc.asset_type as SupportedAssetType)
                        : "document"
                    }
                    file_type={get_content_type(
                      doc.content_type ?? "application/pdf"
                    )}
                    width={16}
                    height={16}
                  />
                )}
              </IconWrapper>
              {doc.asset_name ?? t("Document")}
            </DocumentTag>
          );
        })}
      </TagsContainer>
    );
  };

  return (
    <>
      <BeforeAfterContainer
        attributeName={attributeName}
        beforeContent={renderDocs(before, true)}
        afterContent={renderDocs(after, false)}
      />
      {assetForPreview && (
        <DocumentPreview
          assets={[assetForPreview]}
          selected_asset_idx={0}
          onCancelPreview={() => setSelectedAssetId(null)}
        />
      )}
    </>
  );
}

function UserGroupedChange({
  before,
  after,
  attributeName,
}: GroupedChangeProps) {
  const theme = useTheme();
  const beforeUsers = before
    ?.map((v) => v.user)
    .filter((u): u is User => u !== undefined);
  const afterUsers = after
    ?.map((v) => v.user)
    .filter((u): u is User => u !== undefined);

  return (
    <BeforeAfterContainer
      attributeName={attributeName}
      beforeContent={renderUserChips(
        beforeUsers,
        true,
        beforeUsers,
        afterUsers,
        theme
      )}
      afterContent={renderUserChips(
        afterUsers,
        false,
        beforeUsers,
        afterUsers,
        theme
      )}
    />
  );
}

type RefLike = { id: string; name: string };

function useReferenceHoverState() {
  const [hoveredBeforeIndex, setHoveredBeforeIndex] =
    useState<number | null>(null);
  const [hoveredAfterIndex, setHoveredAfterIndex] =
    useState<number | null>(null);
  const [beforeCoords, setBeforeCoords] = useState<
    Record<number, { top: number; left: number }>
  >({});
  const [afterCoords, setAfterCoords] = useState<
    Record<number, { top: number; left: number }>
  >({});

  const calcFixedCoords = useCallback((element: HTMLLIElement | null) => {
    if (!element) return { top: 0, left: 0 };
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const cardHeight = 400;
    const cardWidth = 360;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const placeAbove = spaceBelow < cardHeight && spaceAbove > spaceBelow;
    const top = placeAbove ? rect.top - cardHeight - 8 : rect.bottom + 8;
    const minLeft = 8;
    const maxLeft = Math.max(minLeft, viewportWidth - cardWidth - 8);
    const spaceRight = viewportWidth - rect.right;
    const spaceLeft = rect.left;
    const placeRight = spaceRight >= cardWidth || spaceRight >= spaceLeft;
    const desiredLeft = placeRight ? rect.right + 8 : rect.left - cardWidth - 8;
    const left = Math.min(maxLeft, Math.max(minLeft, desiredLeft));
    return { top: Math.max(8, top), left };
  }, []);

  return {
    hoveredBeforeIndex,
    hoveredAfterIndex,
    setHoveredBeforeIndex,
    setHoveredAfterIndex,
    beforeCoords,
    afterCoords,
    setBeforeCoords,
    setAfterCoords,
    calcFixedCoords,
  };
}

function ReferenceGroupedChange<T extends RefLike>({
  beforeItems,
  afterItems,
  attributeName,
  linkFor,
  isPreviewable,
  renderPreview,
}: {
  beforeItems: T[];
  afterItems: T[];
  attributeName?: string;
  linkFor: (item: T) => string;
  isPreviewable?: (item: T) => boolean;
  renderPreview: (item: T) => React.ReactNode;
}) {
  const hoverState = useReferenceHoverState();
  const {
    hoveredBeforeIndex,
    hoveredAfterIndex,
    setHoveredBeforeIndex,
    setHoveredAfterIndex,
    beforeCoords,
    afterCoords,
    setBeforeCoords,
    setAfterCoords,
    calcFixedCoords,
  } = hoverState;

  const isRemoved = (item: T): boolean =>
    !afterItems.some((afterItem) => afterItem.id === item.id);
  const isAdded = (item: T): boolean =>
    !beforeItems.some((beforeItem) => beforeItem.id === item.id);

  const renderSide = (items: T[], side: "before" | "after") => {
    if (items.length === 0) return EMPTY_VALUE;
    return (
      <ul style={{ listStyle: "inside", margin: 0, padding: 0 }}>
        {items.map((item, index) => {
          const isHovered =
            side === "before"
              ? hoveredBeforeIndex === index
              : hoveredAfterIndex === index;
          const coords = side === "before" ? beforeCoords : afterCoords;
          const setHovered =
            side === "before" ? setHoveredBeforeIndex : setHoveredAfterIndex;
          const setCoords =
            side === "before" ? setBeforeCoords : setAfterCoords;
          const previewable = isPreviewable ? isPreviewable(item) : true;

          return (
            <HistoryListItem
              key={`${side}-${item.id}-${index}`}
              $removed={side === "before" ? isRemoved(item) : false}
              $added={side === "after" ? isAdded(item) : false}
              onMouseEnter={(e) => {
                if (!previewable) return;
                setHovered(index);
                const position = calcFixedCoords(
                  e.currentTarget as HTMLLIElement
                );
                setCoords((prev) => ({ ...prev, [index]: position }));
              }}
              onMouseLeave={() => setHovered(null)}
              style={{ position: "relative" }}
            >
              <LinkAttr to={linkFor(item)}>{item.name}</LinkAttr>
              {previewable &&
                isHovered &&
                coords[index] &&
                createPortal(
                  <FixedCardPreview
                    style={{
                      position: "fixed",
                      top: coords[index].top,
                      left: coords[index].left,
                    }}
                  >
                    {renderPreview(item)}
                  </FixedCardPreview>,
                  document.body
                )}
            </HistoryListItem>
          );
        })}
      </ul>
    );
  };

  return (
    <ChangeContainer>
      <StateColumn>
        <StateLabel>{beforeStateLabel(attributeName)}</StateLabel>
        <StateValue>{renderSide(beforeItems, "before")}</StateValue>
      </StateColumn>
      <StateColumn>
        <StateLabel>{afterStateLabel(attributeName)}</StateLabel>
        <StateValue>{renderSide(afterItems, "after")}</StateValue>
      </StateColumn>
    </ChangeContainer>
  );
}

function ProductReferenceGroupedChange({
  before,
  after,
  attributeName,
}: GroupedChangeProps) {
  const { adminPath } = useRoutePath();
  const beforeProducts = before
    ?.map((v) => v.product_ref)
    .filter((p): p is ProductReference => p !== undefined);
  const afterProducts = after
    ?.map((v) => v.product_ref)
    .filter((p): p is ProductReference => p !== undefined);

  return (
    <ReferenceGroupedChange
      beforeItems={beforeProducts}
      afterItems={afterProducts}
      attributeName={attributeName}
      linkFor={(product) =>
        `${adminPath}/pim/products/${product.product_number ?? product.id}`
      }
      renderPreview={(product) => <HistoryProductCard product={product} />}
    />
  );
}

function objectRefToHistoryProductCardData(
  ref: ObjectReferenceResult
): HistoryProductCardData {
  return {
    id: ref.id,
    name: ref.name,
    product_number: ref.object_number ?? undefined,
    image_url: ref.image_url ?? undefined,
    cover_image: ref.image_url ?? undefined,
  };
}

function ObjectReferenceGroupedChange({
  before,
  after,
  attributeName,
}: GroupedChangeProps) {
  const { adminPath } = useRoutePath();
  const beforeRefs = before
    ?.map((v) => v.object_ref)
    .filter((r): r is ObjectReferenceResult => r != null);
  const afterRefs = after
    ?.map((v) => v.object_ref)
    .filter((r): r is ObjectReferenceResult => r != null);

  const linkFor = (ref: ObjectReferenceResult) =>
    ref.object_type === "product"
      ? `${adminPath}/pim/products/${ref.object_number ?? ref.id}`
      : getObjectReferenceDetailPath(adminPath, ref.object_type, ref.id);

  return (
    <ReferenceGroupedChange
      beforeItems={beforeRefs}
      afterItems={afterRefs}
      attributeName={attributeName}
      linkFor={linkFor}
      isPreviewable={(ref) => ref.object_type === "product"}
      renderPreview={(ref) => (
        <HistoryProductCard product={objectRefToHistoryProductCardData(ref)} />
      )}
    />
  );
}

function renderToggle(
  checked: boolean,
  attributeName: string | undefined,
  isBefore: boolean
): React.ReactNode {
  return (
    <ToggleContainer>
      <ToggleSwitchV2
        checked={checked}
        onChange={() => {}}
        disabled={true}
        label={attributeName || ""}
        name={`${isBefore ? "before" : "after"}-${attributeName}`}
      />
    </ToggleContainer>
  );
}

function renderVisibilityIcon(
  isVisible: boolean | null | undefined,
  theme: ReturnType<typeof useTheme>,
  t: TFunction
): React.ReactNode {
  if (isVisible === null || isVisible === undefined) {
    return EMPTY_VALUE;
  }

  const Icon = isVisible ? VisibleIcon : NonVisibleIcon;
  const iconColor = isVisible
    ? theme.resolve("color_icon_success")
    : theme.resolve("color_icon_error");

  const text = isVisible
    ? t("Link is accessible by external users")
    : t("Link is not accessible by external users");

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <Icon fill={iconColor} width={20} height={20} />
      <span>{text}</span>
    </div>
  );
}

function VisibilityIconAttributeChange({
  before,
  after,
  attributeName,
}: {
  before?: boolean | null;
  after?: boolean | null;
  attributeName?: string;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <BeforeAfterContainer
      attributeName={attributeName}
      beforeContent={renderVisibilityIcon(before, theme, t)}
      afterContent={renderVisibilityIcon(after, theme, t)}
    />
  );
}

function ToggleAttributeChange({
  before,
  after,
  attributeName,
}: {
  before: boolean;
  after: boolean;
  attributeName?: string;
}) {
  const displayName =
    attributeName === BUYER_CUSTOM_SKU_ATTRIBUTE_NAME ? "" : attributeName;
  return (
    <BeforeAfterContainer
      attributeName={displayName}
      beforeContent={renderToggle(before, attributeName, true)}
      afterContent={renderToggle(after, attributeName, false)}
    />
  );
}

export function renderChip(
  value: string,
  isChanged: boolean,
  isBefore: boolean,
  theme: ReturnType<typeof useTheme>
): React.ReactNode {
  if (!value) {
    return EMPTY_VALUE;
  }

  return (
    <div>
      <span>
        <Chip
          name={value}
          data-chip={value}
          key={value}
          active={false}
          onClick={() => {}}
          style={{
            backgroundColor: isChanged
              ? isBefore
                ? theme.resolve("color_bg_surface_error_diff")
                : theme.resolve("color_bg_surface_success_diff")
              : undefined,
          }}
        >
          {value}
        </Chip>
      </span>
    </div>
  );
}

function SingleSelectAttributeChange({
  before,
  after,
  attributeName,
}: {
  before: string;
  after: string;
  attributeName?: string;
}) {
  const theme = useTheme();
  const beforeValue = formatValueForDisplay(before);
  const afterValue = formatValueForDisplay(after);
  const isChanged = beforeValue !== afterValue;

  return (
    <BeforeAfterContainer
      attributeName={attributeName}
      beforeContent={renderChip(beforeValue, isChanged, true, theme)}
      afterContent={renderChip(afterValue, isChanged, false, theme)}
    />
  );
}

function renderLinkDisplayText(
  beforeText: string,
  afterText: string,
  isBefore: boolean
): React.ReactNode {
  const currentText = isBefore ? beforeText : afterText;

  if (!currentText) {
    return <DiffText>{EMPTY_VALUE}</DiffText>;
  }

  if (!beforeText && !afterText) {
    return <DiffText>{EMPTY_VALUE}</DiffText>;
  }

  const diffParts = computeSmartDiff(beforeText || "", afterText || "");
  const filteredParts = isBefore
    ? diffParts.filter((part) => !part.added)
    : diffParts.filter((part) => !part.removed);

  return (
    <span>
      {filteredParts.map((part, index) => (
        <DiffText key={index} removed={part.removed} added={part.added}>
          {part.value}
        </DiffText>
      ))}
    </span>
  );
}

function renderLinkUrl(
  beforeUrl: string,
  afterUrl: string,
  isBefore: boolean
): React.ReactNode {
  const currentUrl = isBefore ? beforeUrl : afterUrl;

  if (!currentUrl) {
    return <DiffText>{EMPTY_VALUE}</DiffText>;
  }

  if (!beforeUrl && !afterUrl) {
    return <DiffText>{EMPTY_VALUE}</DiffText>;
  }

  const diffParts = computeSmartDiff(beforeUrl || "", afterUrl || "");
  const filteredParts = isBefore
    ? diffParts.filter((part) => !part.added)
    : diffParts.filter((part) => !part.removed);

  return (
    <span>
      {filteredParts.map((part, index) => (
        <DiffText key={index} removed={part.removed} added={part.added}>
          <a href={currentUrl} target="_blank" rel="noopener noreferrer">
            {part.value}
          </a>
        </DiffText>
      ))}
    </span>
  );
}

function renderLinkContent(
  beforeData: { displayText: string; url: string },
  afterData: { displayText: string; url: string },
  attributeName: string | undefined,
  isBefore: boolean
): React.ReactNode {
  const labelPrefix = attributeName
    ? isBefore
      ? beforeStateLabel(attributeName)
      : afterStateLabel(attributeName)
    : "";
  const currentUrl = isBefore ? beforeData.url : afterData.url;
  const currentDisplayText = isBefore
    ? beforeData.displayText
    : afterData.displayText;

  const displayTextLabel = labelPrefix
    ? `${labelPrefix} - Display Text`
    : "Display Text";
  const urlLabel = labelPrefix ? `${labelPrefix} - URL` : "URL";

  return (
    <StateValue>
      <div style={{ marginTop: spacingTokens.spacing_md }}>
        <StateLabel>{displayTextLabel}</StateLabel>
        {currentDisplayText ? (
          renderLinkDisplayText(
            beforeData.displayText,
            afterData.displayText,
            isBefore
          )
        ) : (
          <DiffText>{EMPTY_VALUE}</DiffText>
        )}
      </div>
      <div style={{ marginTop: spacingTokens.spacing_md }}>
        <StateLabel>{urlLabel}</StateLabel>
        {currentUrl ? (
          renderLinkUrl(beforeData.url, afterData.url, isBefore)
        ) : (
          <DiffText>{EMPTY_VALUE}</DiffText>
        )}
      </div>
    </StateValue>
  );
}

function LinkAttributeChange({
  before,
  after,
  attributeName,
}: {
  before: string;
  after: string;
  attributeName?: string;
}) {
  const beforeData = imageUrlAndDisplayText(before);
  const afterData = imageUrlAndDisplayText(after);

  return (
    <ChangeContainer>
      <StateColumn>
        {renderLinkContent(beforeData, afterData, attributeName, true)}
      </StateColumn>
      <StateColumn>
        {renderLinkContent(beforeData, afterData, attributeName, false)}
      </StateColumn>
    </ChangeContainer>
  );
}

function renderImageContent(
  imageData: {
    signedUrl?: string;
    previewUrl?: string;
    caption?: string;
    dimensions?: string;
  } | null,
  beforeData: {
    signedUrl?: string;
    previewUrl?: string;
    caption?: string;
    dimensions?: string;
  } | null,
  afterData: {
    signedUrl?: string;
    previewUrl?: string;
    caption?: string;
    dimensions?: string;
  } | null,
  attributeName: string | undefined,
  isBefore: boolean,
  t: TFunction
): React.ReactNode {
  const labelPrefix = isBefore
    ? beforeStateLabel(attributeName)
    : afterStateLabel(attributeName);

  return (
    <div>
      {imageData?.signedUrl || imageData?.previewUrl ? (
        <AssetImagePreview asset={imageData} t={t} />
      ) : (
        <HistoryNoImagePlaceholder>
          <HistoryNoImageIcon />
          <HistoryNoImageText>No Image</HistoryNoImageText>
        </HistoryNoImagePlaceholder>
      )}
      <ImageInfo>
        <div style={{ marginTop: spacingTokens.spacing_md }}>
          <StateLabel>{labelPrefix} - Caption</StateLabel>
          <StateValue>
            {renderTextWithDiff(
              beforeData?.caption || "",
              afterData?.caption || "",
              isBefore
            )}
          </StateValue>
        </div>
        <div style={{ marginTop: spacingTokens.spacing_md }}>
          <StateLabel>{labelPrefix} - Size</StateLabel>
          <StateValue>
            {renderTextWithDiff(
              beforeData?.dimensions || "",
              afterData?.dimensions || "",
              isBefore
            )}
          </StateValue>
        </div>
      </ImageInfo>
    </div>
  );
}

function ImageAttributeChange({
  before,
  after,
  attributeName,
  t,
}: {
  before: string;
  after: string;
  attributeName?: string;
  t: TFunction;
}) {
  const beforeData = parseImageData(before);
  const afterData = parseImageData(after);

  return (
    <BeforeAfterContainer
      attributeName={attributeName}
      beforeContent={renderImageContent(
        beforeData,
        beforeData,
        afterData,
        attributeName,
        true,
        t
      )}
      afterContent={renderImageContent(
        afterData,
        beforeData,
        afterData,
        attributeName,
        false,
        t
      )}
    />
  );
}

const extractValueFromHistory = (
  val:
    | ActivityHistoryValue
    | ActivityHistoryValue[]
    | ValueType
    | null
    | undefined
): string | number | null => {
  if (isNullOrUndefined(val)) return null;

  if (isPrimitive(val)) {
    return typeof val === "boolean" ? String(val) : val;
  }

  if (Array.isArray(val)) {
    const firstItem = getFirstElement(val, hasValueProperty);
    if (firstItem) {
      const value = firstItem.value;
      return isPrimitive(value)
        ? typeof value === "boolean"
          ? String(value)
          : value
        : null;
    }
    return null;
  }

  if (hasValueProperty(val)) {
    const value = val.value;
    return isPrimitive(value)
      ? typeof value === "boolean"
        ? String(value)
        : value
      : null;
  }

  return null;
};

const toActivityHistoryValueArray = (
  val:
    | ActivityHistoryValue
    | ActivityHistoryValue[]
    | ValueType
    | null
    | undefined
): ActivityHistoryValue[] => {
  if (isNullOrUndefined(val)) return [];

  if (Array.isArray(val)) {
    if (isActivityHistoryValueArray(val)) {
      return val.filter(isActivityHistoryValue);
    }
    return [];
  }

  if (isActivityHistoryValue(val)) {
    return [val];
  }

  return [];
};

const extractLinkValue = (
  val:
    | ActivityHistoryValue
    | ActivityHistoryValue[]
    | ValueType
    | null
    | undefined
): string | LinkValue => {
  if (isNullOrUndefined(val)) return "";

  if (Array.isArray(val)) {
    const firstVal = getFirstElement(val, hasValueProperty);
    if (firstVal) {
      return isObject(firstVal.value)
        ? (firstVal.value as LinkValue)
        : String(firstVal.value ?? "");
    }
  } else if (hasValueProperty(val)) {
    return isObject(val.value)
      ? (val.value as LinkValue)
      : String(val.value ?? "");
  } else if (isPrimitive(val)) {
    return String(val);
  }

  return "";
};

const extractImageValue = (
  val:
    | ActivityHistoryValue
    | ActivityHistoryValue[]
    | ValueType
    | null
    | undefined
): ActivityHistoryValue | string | null => {
  if (isNullOrUndefined(val)) return null;

  if (Array.isArray(val)) {
    const firstVal = getFirstElement(val, isActivityHistoryValue);
    if (firstVal) {
      return firstVal;
    }
  } else if (isActivityHistoryValue(val)) {
    return val;
  } else if (isString(val)) {
    return val;
  }

  return null;
};

export function UnifiedAttributeChange({
  subItem,
  before,
  after,
  attributeName,
  inputType,
}: {
  subItem?: ResultItem["subItems"][0];
  before?: ValueType | ActivityHistoryValue | ActivityHistoryValue[] | null;
  after?: ValueType | ActivityHistoryValue | ActivityHistoryValue[] | null;
  attributeName?: string;
  inputType?: string;
}): JSX.Element {
  const { t } = useTranslation();
  let finalBefore:
    | ValueType
    | ActivityHistoryValue
    | ActivityHistoryValue[]
    | null;
  let finalAfter:
    | ValueType
    | ActivityHistoryValue
    | ActivityHistoryValue[]
    | null;
  let finalAttributeName: string | undefined;
  let finalInputType: string;
  let finalAddressMode: ResultItem["subItems"][0]["addressMode"];

  if (subItem) {
    finalBefore = subItem.beforeValue;
    finalAfter = subItem.afterValue;
    finalAttributeName = subItem.attributeName;
    finalInputType = subItem.inputType;
    finalAddressMode = subItem.addressMode;
  } else {
    finalBefore = before ?? null;
    finalAfter = after ?? null;
    finalAttributeName = attributeName;
    finalInputType = inputType ?? "text";
    finalAddressMode = undefined;
  }

  const simpleTextTypes = [
    "text",
    "numeric",
    "multiline",
    "html",
    "multiline_entry",
    "form_field",
  ];

  // Handle simple text types
  if (simpleTextTypes.includes(finalInputType)) {
    let beforeProcessed = extractValueFromHistory(finalBefore);
    let afterProcessed = extractValueFromHistory(finalAfter);

    if (
      finalInputType === "html" ||
      finalInputType === "multiline" ||
      finalInputType === "multiline_entry"
    ) {
      const beforeHtml = isString(beforeProcessed) ? beforeProcessed : "";
      const afterHtml = isString(afterProcessed) ? afterProcessed : "";

      return htmlAttributeChange({
        before: beforeHtml,
        after: afterHtml,
        attributeName: finalAttributeName,
      });
    }

    const beforeText = formatValueForDisplay(beforeProcessed);
    const afterText = formatValueForDisplay(afterProcessed);

    return attributeChange({
      before: beforeText,
      after: afterText,
      attributeName: finalAttributeName,
    });
  }

  // Handle date
  if (finalInputType === "date") {
    const beforeVal = extractValueFromHistory(finalBefore);
    const afterVal = extractValueFromHistory(finalAfter);
    const beforeDate = beforeVal ? formatDate(String(beforeVal)) : "";
    const afterDate = afterVal ? formatDate(String(afterVal)) : "";
    return attributeChange({
      before: beforeDate,
      after: afterDate,
      attributeName: finalAttributeName,
    });
  }

  // Handle toggle
  if (finalInputType === "toggle") {
    const extractBoolean = (
      val:
        | ActivityHistoryValue
        | ActivityHistoryValue[]
        | ValueType
        | null
        | undefined
    ): boolean | null | undefined => {
      if (isNullOrUndefined(val)) return null;
      if (typeof val === "boolean") return val;
      if (Array.isArray(val) && val.length > 0) {
        const firstItem = val[0];
        if (isActivityHistoryValue(firstItem)) {
          const value = firstItem.value;
          if (typeof value === "boolean") return value;
          if (typeof value === "string") {
            if (value.toLowerCase() === "true") return true;
            if (value.toLowerCase() === "false") return false;
          }
        }
        return null;
      }
      if (isActivityHistoryValue(val) || hasValueProperty(val)) {
        const value = val.value;
        if (typeof value === "boolean") return value;
        if (typeof value === "string") {
          if (value.toLowerCase() === "true") return true;
          if (value.toLowerCase() === "false") return false;
        }
        return null;
      }
      return null;
    };

    const isExternalLinkVisibility =
      finalAttributeName === t("Is Visible") ||
      (isActivityHistoryValue(finalBefore) &&
        finalBefore.attribute_id === "external_link_is_visible") ||
      (isActivityHistoryValue(finalAfter) &&
        finalAfter.attribute_id === "external_link_is_visible");

    if (isExternalLinkVisibility) {
      return VisibilityIconAttributeChange({
        before: extractBoolean(finalBefore),
        after: extractBoolean(finalAfter),
        attributeName: finalAttributeName,
      });
    }

    return ToggleAttributeChange({
      before: extractBoolean(finalBefore) ?? false,
      after: extractBoolean(finalAfter) ?? false,
      attributeName: finalAttributeName,
    });
  }

  // Handle single_select
  if (finalInputType === "single_select") {
    const beforeVal = extractValueFromHistory(finalBefore);
    const afterVal = extractValueFromHistory(finalAfter);
    return SingleSelectAttributeChange({
      before: String(beforeVal ?? ""),
      after: String(afterVal ?? ""),
      attributeName: finalAttributeName,
    });
  }

  //Handle url
  if (finalInputType === "url") {
    const beforeUrl = extractLinkValue(finalBefore);
    const afterUrl = extractLinkValue(finalAfter);
    return UrlAttributeChange({
      before: isObject(beforeUrl) ? JSON.stringify(beforeUrl) : beforeUrl,
      after: isObject(afterUrl) ? JSON.stringify(afterUrl) : afterUrl,
      attributeName: finalAttributeName,
    });
  }
  // Handle link
  if (finalInputType === "link") {
    const beforeLink = extractLinkValue(finalBefore);
    const afterLink = extractLinkValue(finalAfter);

    return LinkAttributeChange({
      before: isObject(beforeLink) ? JSON.stringify(beforeLink) : beforeLink,
      after: isObject(afterLink) ? JSON.stringify(afterLink) : afterLink,
      attributeName: finalAttributeName,
    });
  }

  // Handle image
  if (finalInputType === "image") {
    const beforeImage = extractImageValue(finalBefore);
    const afterImage = extractImageValue(finalAfter);

    const beforeStr = beforeImage
      ? isString(beforeImage)
        ? beforeImage
        : JSON.stringify(beforeImage)
      : "";
    const afterStr = afterImage
      ? isString(afterImage)
        ? afterImage
        : JSON.stringify(afterImage)
      : "";

    return ImageAttributeChange({
      before: beforeStr,
      after: afterStr,
      attributeName: finalAttributeName,
      t,
    });
  }

  if (finalInputType === "file") {
    const beforeAsset = finalBefore as Assets | null;
    const afterAsset = finalAfter as Assets | null;

    if (!beforeAsset && isNonNullObject(afterAsset)) {
      return (
        <AssetAddedChange
          asset={afterAsset}
          attributeName={finalAttributeName}
        />
      );
    }

    if (isNonNullObject(beforeAsset) && !afterAsset) {
      return (
        <AssetRemovedChange
          asset={beforeAsset}
          attributeName={finalAttributeName}
        />
      );
    }

    if (isNonNullObject(beforeAsset) && isNonNullObject(afterAsset)) {
      return <AssetUpdatedChange before={beforeAsset} after={afterAsset} />;
    }
  }

  if (finalInputType === "accessibility") {
    const beforeAsset = finalBefore as Assets | null;
    const afterAsset = finalAfter as Assets | null;

    if (isNonNullObject(beforeAsset) && isNonNullObject(afterAsset)) {
      return (
        <AccessibilityAttributeChange before={beforeAsset} after={afterAsset} />
      );
    }
  }

  if (finalInputType === "product_reference") {
    const beforeArray = toActivityHistoryValueArray(finalBefore);
    const afterArray = toActivityHistoryValueArray(finalAfter);
    return (
      <ProductReferenceGroupedChange
        before={beforeArray}
        after={afterArray}
        attributeName={finalAttributeName}
      />
    );
  }

  if (finalInputType === "object_reference") {
    const beforeArray = toActivityHistoryValueArray(finalBefore);
    const afterArray = toActivityHistoryValueArray(finalAfter);
    return (
      <ObjectReferenceGroupedChange
        before={beforeArray}
        after={afterArray}
        attributeName={finalAttributeName}
      />
    );
  }

  if (finalInputType === "company_hierarchy") {
    const beforeHierarchy = finalBefore as CompanyHierarchyValue | null;
    const afterHierarchy = finalAfter as CompanyHierarchyValue | null;
    return (
      <CompanyHierarchyActivity
        parentEntity={{
          before: beforeHierarchy?.parentEntity as EntityPreview | null,
          after: afterHierarchy?.parentEntity as EntityPreview | null,
        }}
        subsidiaryActions={{
          before: beforeHierarchy?.subsidiaryActions,
          after: afterHierarchy?.subsidiaryActions,
        }}
      />
    );
  }

  if (finalInputType === "multi_select") {
    const beforeArray = toActivityHistoryValueArray(finalBefore);
    const afterArray = toActivityHistoryValueArray(finalAfter);
    return (
      <MultiSelectGroupedChange
        before={beforeArray}
        after={afterArray}
        attributeName={finalAttributeName}
      />
    );
  }
  if (finalInputType === "user") {
    const beforeArray = toActivityHistoryValueArray(finalBefore);
    const afterArray = toActivityHistoryValueArray(finalAfter);
    return (
      <UserGroupedChange
        before={beforeArray}
        after={afterArray}
        attributeName={finalAttributeName}
      />
    );
  }

  // Handle address
  if (finalInputType === "address") {
    const extractAddress = (val: typeof finalBefore): AddressValue | null => {
      if (isNullOrUndefined(val)) return null;
      if (isActivityHistoryValue(val) && isNonNullObject(val.value)) {
        return val.value as AddressValue;
      }
      if (isNonNullObject(val) && !Array.isArray(val)) {
        return val as AddressValue;
      }
      return null;
    };

    return (
      <AddressTable
        before={extractAddress(finalBefore)}
        after={extractAddress(finalAfter)}
        addressMode={finalAddressMode}
      />
    );
  }

  if (finalInputType === "related_entities_updated") {
    const afterSections =
      (finalAfter as RelatedEntitiesValue | null)?.sections ?? [];
    const beforeSections =
      (finalBefore as RelatedEntitiesValue | null)?.sections ?? [];

    return (
      <RelatedEntitiesActivity
        sections={{
          after: afterSections,
          before: beforeSections,
        }}
      />
    );
  }

  // Handle document attribute
  if (finalInputType === "document") {
    const beforeArray = toActivityHistoryValueArray(finalBefore);
    const afterArray = toActivityHistoryValueArray(finalAfter);
    return (
      <DocumentGroupedChange
        before={beforeArray}
        after={afterArray}
        attributeName={finalAttributeName}
      />
    );
  }

  // Fallback for unknown types
  const beforeVal = extractValueFromHistory(finalBefore);
  const afterVal = extractValueFromHistory(finalAfter);
  const beforeValue = formatValueForDisplay(beforeVal);
  const afterValue = formatValueForDisplay(afterVal);
  return attributeChange({
    before: beforeValue,
    after: afterValue,
    attributeName: finalAttributeName,
  });
}
